/**
 * Centralized Icon System
 *
 * Single source of truth for all icons, backed by @heroicons/react v2.
 * - Use named exports: <Icon.User />, <Icon.Trash />, etc. (existing pattern)
 * - Or use generic component: <Icon name="user" size={20} className="..." />
 *
 * Do NOT import from @heroicons/react directly in feature/page components.
 * Always import from this module or its re-export path.
 */

import React from 'react';
import {
  UserIcon,
  UsersIcon,
  UserCircleIcon,
  TrashIcon,
  XMarkIcon,
  CheckCircleIcon,
  CheckBadgeIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  Squares2X2Icon,
  RectangleStackIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  ArrowsUpDownIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowRightOnRectangleIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  DocumentCheckIcon,
  DocumentChartBarIcon,
  PhotoIcon,
  PencilIcon,
  PencilSquareIcon,
  PlusIcon,
  PlusCircleIcon,
  MinusIcon,
  Bars3Icon,
  Bars2Icon,
  BellIcon,
  Cog6ToothIcon,
  LockClosedIcon,
  LockOpenIcon,
  MapPinIcon,
  ClockIcon,
  CubeIcon,
  CircleStackIcon,
  BanknotesIcon,
  CreditCardIcon,
  ShoppingCartIcon,
  TagIcon,
  PrinterIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
  KeyIcon,
  IdentificationIcon,
  ShareIcon,
  TableCellsIcon,
  FolderOpenIcon,
  TrophyIcon,
  TruckIcon,
  PuzzlePieceIcon,
  WrenchScrewdriverIcon,
  SparklesIcon,
  ClipboardDocumentListIcon,
  NoSymbolIcon,
  PowerIcon,
  EllipsisVerticalIcon,
  FaceSmileIcon,
  AdjustmentsHorizontalIcon,
  StarIcon,
  WalletIcon,
  ScaleIcon,
  BuildingOfficeIcon,
  BuildingStorefrontIcon,
  BuildingLibraryIcon,
  GiftIcon,
  ReceiptPercentIcon,
  ReceiptRefundIcon,
  QueueListIcon,
  ListBulletIcon,
} from '@heroicons/react/24/solid';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NamedIconProps {
  size?: number;
  className?: string;
}

/** Heroicons v2 components are ForwardRefExoticComponents with this shape */
type HeroIconType = React.ComponentType<{
  width?: number | string;
  height?: number | string;
  className?: string;
}>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Wraps a Heroicon into a simple component that accepts optional size/className.
 * The returned component is callable with zero arguments (compatible with `() => any`)
 * because all props have defaults.
 */
function wrap(HeroIcon: HeroIconType, defaultSize = 20) {
  const IconWrapper = (props?: NamedIconProps) => {
    const { size = defaultSize, className } = props ?? {};
    return <HeroIcon width={size} height={size} className={className} />;
  };
  return IconWrapper;
}

// ---------------------------------------------------------------------------
// Named icon components  (backward-compatible API: <Icon.User />, etc.)
// ---------------------------------------------------------------------------

// ── Layout & Navigation ────────────────────────────────────────────────────
const Dashboard = wrap(Squares2X2Icon as HeroIconType);
const Menu = wrap(Bars3Icon as HeroIconType);
const DragPoint = wrap(Bars2Icon as HeroIconType);
const ArrowDown = wrap(ChevronDownIcon as HeroIconType, 10);
const ArrowLeft = wrap(ArrowLeftIcon as HeroIconType);
const Up = wrap(ChevronUpIcon as HeroIconType);
const Down = wrap(ChevronDownIcon as HeroIconType);
const Prev = wrap(ChevronLeftIcon as HeroIconType);
const Next = wrap(ChevronRightIcon as HeroIconType);
const Dropdown = wrap(ChevronDownIcon as HeroIconType, 16);

// ── User / Auth ────────────────────────────────────────────────────────────
const User = wrap(UserIcon as HeroIconType);
const Account = wrap(UserCircleIcon as HeroIconType);
const Profile = wrap(IdentificationIcon as HeroIconType);
const KYC = wrap(IdentificationIcon as HeroIconType);
const Logout = wrap(ArrowRightOnRectangleIcon as HeroIconType);
const Power = wrap(PowerIcon as HeroIconType);
const LockUserIcon = wrap(LockClosedIcon as HeroIconType);
const UnlockUserIcon = wrap(LockOpenIcon as HeroIconType);
const Lock = wrap(LockClosedIcon as HeroIconType);

// ── Actions ────────────────────────────────────────────────────────────────
const Edit = wrap(PencilSquareIcon as HeroIconType);
const ActionEditIcon = wrap(PencilIcon as HeroIconType);
const ActionMoreIcon = wrap(EllipsisVerticalIcon as HeroIconType);
const ActionOrders = wrap(ClipboardDocumentListIcon as HeroIconType);
const Copy = wrap(DocumentDuplicateIcon as HeroIconType);
const Trash = wrap(TrashIcon as HeroIconType);
const Remove = wrap(XMarkIcon as HeroIconType);
const Close = wrap(XMarkIcon as HeroIconType);
const ClosePopup = wrap(XMarkIcon as HeroIconType);
const Plus = wrap(PlusIcon as HeroIconType);
const Minus = wrap(MinusIcon as HeroIconType);
const AddNewIcon = wrap(PlusCircleIcon as HeroIconType);
const PlusMinus = wrap(AdjustmentsHorizontalIcon as HeroIconType);
const Reload = wrap(ArrowPathIcon as HeroIconType);
const Update = wrap(ArrowPathIcon as HeroIconType);
const Print = wrap(PrinterIcon as HeroIconType);
const Filter = wrap(FunnelIcon as HeroIconType);
const Search = wrap(MagnifyingGlassIcon as HeroIconType);
const Upload = wrap(ArrowUpTrayIcon as HeroIconType);
const Eye = wrap(EyeIcon as HeroIconType);
const Visible = wrap(EyeIcon as HeroIconType);
const Invisible = wrap(EyeSlashIcon as HeroIconType);
const ViewDetailIcon = wrap(EyeIcon as HeroIconType);
const BlockIcon = wrap(NoSymbolIcon as HeroIconType);
const UnblockIcon = wrap(CheckCircleIcon as HeroIconType);
const ConfirmStatus = wrap(CheckBadgeIcon as HeroIconType);
const TimestampIcon = wrap(ClockIcon as HeroIconType);

// ── Status / Feedback ──────────────────────────────────────────────────────
const Checked = wrap(CheckCircleIcon as HeroIconType);
const Info = wrap(InformationCircleIcon as HeroIconType);
const Warning = wrap(ExclamationTriangleIcon as HeroIconType);
const Danger = wrap(ExclamationCircleIcon as HeroIconType);
const Error = wrap(ExclamationCircleIcon as HeroIconType);

// ── Finance / Business ────────────────────────────────────────────────────
const Wallet = wrap(WalletIcon as HeroIconType);
const Trade = wrap(ArrowsRightLeftIcon as HeroIconType);
const Exchange = wrap(ArrowsRightLeftIcon as HeroIconType);
const CoinExchange = wrap(ArrowsRightLeftIcon as HeroIconType);
const Deposit = wrap(ArrowDownTrayIcon as HeroIconType);
const Withdraw = wrap(ArrowUpTrayIcon as HeroIconType);
const Transfer = wrap(ArrowsUpDownIcon as HeroIconType);
const BalanceIcon = wrap(BanknotesIcon as HeroIconType);
const Transaction = wrap(CreditCardIcon as HeroIconType);
const TransactionIcon = wrap(CreditCardIcon as HeroIconType);
const Transaction2Icon = wrap(BanknotesIcon as HeroIconType);
const CreditCard = wrap(CreditCardIcon as HeroIconType);

// ── Data / Reports ─────────────────────────────────────────────────────────
const Data = wrap(CircleStackIcon as HeroIconType);
const Report = wrap(DocumentChartBarIcon as HeroIconType);
const Table = wrap(TableCellsIcon as HeroIconType);
const HistoryIcon = wrap(ClockIcon as HeroIconType);

// ── Products / Commerce ──────────────────────────────────────────────────
const Product = wrap(CubeIcon as HeroIconType);
const Category = wrap(TagIcon as HeroIconType);
const Cart = wrap(ShoppingCartIcon as HeroIconType);
const Collection = wrap(RectangleStackIcon as HeroIconType);
const OrderIcon = wrap(DocumentTextIcon as HeroIconType);
const SupplierIcon = wrap(TruckIcon as HeroIconType);

// ── Prices ──────────────────────────────────────────────────────────────────
const PriceUp = wrap(ArrowTrendingUpIcon as HeroIconType);
const PriceDown = wrap(ArrowTrendingDownIcon as HeroIconType);
const SwapUp = wrap(ArrowUpIcon as HeroIconType);
const SwapDown = wrap(ArrowDownIcon as HeroIconType);
const SwapUpIcon = wrap(ArrowTrendingUpIcon as HeroIconType);
const SwapDownIcon = wrap(ArrowTrendingDownIcon as HeroIconType);

// ── Stats ──────────────────────────────────────────────────────────────────
const TotalPackageIcon = wrap(CubeIcon as HeroIconType);
const TotalRefundIcon = wrap(ArrowPathIcon as HeroIconType);
const TotalVolumeIcon = wrap(ScaleIcon as HeroIconType);
const TotalOrdersIcon = wrap(ShoppingCartIcon as HeroIconType);
const UserParticipationIcon = wrap(UsersIcon as HeroIconType);

// ── Notifications / Comms ─────────────────────────────────────────────────
const Notification = wrap(BellIcon as HeroIconType);
const Mail = wrap(EnvelopeIcon as HeroIconType);
const VerifyMail = wrap(EnvelopeOpenIcon as HeroIconType);
const Setting = wrap(Cog6ToothIcon as HeroIconType);
const Affiliate = wrap(ShareIcon as HeroIconType);

// ── Game / Entertainment ──────────────────────────────────────────────────
const GameIcon = wrap(PuzzlePieceIcon as HeroIconType);
const GameManagementIcon = wrap(PuzzlePieceIcon as HeroIconType);
const GameOrdersIcon = wrap(ClipboardDocumentListIcon as HeroIconType);
const PlaceGameIcon = wrap(MapPinIcon as HeroIconType);
const ExperienceGameIcon = wrap(SparklesIcon as HeroIconType);
const MaintenanceIcon = wrap(WrenchScrewdriverIcon as HeroIconType);

// ── Content / Structure ───────────────────────────────────────────────────
const Tree = wrap(FolderOpenIcon as HeroIconType);
const Leader = wrap(TrophyIcon as HeroIconType);
const Image = wrap(PhotoIcon as HeroIconType);
const Reaction = wrap(FaceSmileIcon as HeroIconType);
const Place = wrap(MapPinIcon as HeroIconType);

// ── Documents ─────────────────────────────────────────────────────────────
const ClaimIcon = wrap(DocumentCheckIcon as HeroIconType);
const ChangePasswordIcon = wrap(KeyIcon as HeroIconType);

// ── Organizations / Locations ─────────────────────────────────────────────
const Building = wrap(BuildingOfficeIcon as HeroIconType);
const Cooperative = wrap(BuildingOfficeIcon as HeroIconType);
const BankBuilding = wrap(BuildingLibraryIcon as HeroIconType);
const Storefront = wrap(BuildingStorefrontIcon as HeroIconType);

// ── Points / Rewards ──────────────────────────────────────────────────────
const Gift = wrap(GiftIcon as HeroIconType);
const PointTransaction = wrap(ReceiptPercentIcon as HeroIconType);
const WithdrawRequest = wrap(ReceiptRefundIcon as HeroIconType);
const ManageList = wrap(QueueListIcon as HeroIconType);
const BulletList = wrap(ListBulletIcon as HeroIconType);

// ── Checkbox ──────────────────────────────────────────────────────────────
/** Checked checkbox — filled check-circle */
const CheckboxChecked = (props?: NamedIconProps) => {
  const { size = 20, className } = props ?? {};
  return <CheckCircleIcon width={size} height={size} className={className} />;
};

/** Unchecked checkbox — outlined circle */
const CheckboxUnchecked = (props?: NamedIconProps) => {
  const { size = 20, className } = props ?? {};
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      fill='none'
      viewBox='0 0 24 24'
      strokeWidth={1.5}
      stroke='currentColor'
      width={size}
      height={size}
      className={className}
    >
      <circle cx='12' cy='12' r='9' />
    </svg>
  );
};

// ── Loading spinner ───────────────────────────────────────────────────────
/** Custom CSS spinner — heroicons has no spinner variant */
const Loading = (props?: NamedIconProps) => {
  const { className } = props ?? {};
  return (
    <svg
      className={`spinner${className ? ` ${className}` : ''}`}
      viewBox='0 0 50 50'
    >
      <circle
        className='path'
        cx={25}
        cy={25}
        r={20}
        fill='none'
        strokeWidth={5}
      />
    </svg>
  );
};

// ── Misc ───────────────────────────────────────────────────────────────────
const Star = wrap(StarIcon as HeroIconType);

// ---------------------------------------------------------------------------
// Named export namespace  (matches existing `Icon.X` usage pattern)
// ---------------------------------------------------------------------------

export const Icon = {
  // Layout & Navigation
  Dashboard,
  Menu,
  DragPoint,
  ArrowDown,
  ArrowLeft,
  Up,
  Down,
  Prev,
  Next,
  Dropdown,

  // User / Auth
  User,
  Account,
  Profile,
  KYC,
  Logout,
  Power,
  LockUserIcon,
  UnlockUserIcon,
  Lock,

  // Actions
  Edit,
  ActionEditIcon,
  ActionMoreIcon,
  ActionOrders,
  Copy,
  Trash,
  Remove,
  Close,
  ClosePopup,
  Plus,
  Minus,
  AddNewIcon,
  PlusMinus,
  Reload,
  Update,
  Print,
  Filter,
  Search,
  Upload,
  Eye,
  Visible,
  Invisible,
  ViewDetailIcon,
  BlockIcon,
  UnblockIcon,
  ConfirmStatus,
  TimestampIcon,

  // Status / Feedback
  Checked,
  Info,
  Warning,
  Danger,
  Error,

  // Finance / Business
  Wallet,
  Trade,
  Exchange,
  CoinExchange,
  Deposit,
  Withdraw,
  Transfer,
  BalanceIcon,
  Transaction,
  TransactionIcon,
  Transaction2Icon,
  CreditCard,

  // Data / Reports
  Data,
  Report,
  Table,
  HistoryIcon,

  // Products / Commerce
  Product,
  Category,
  Cart,
  Collection,
  OrderIcon,
  SupplierIcon,

  // Prices
  PriceUp,
  PriceDown,
  SwapUp,
  SwapDown,
  SwapUpIcon,
  SwapDownIcon,

  // Stats
  TotalPackageIcon,
  TotalRefundIcon,
  TotalVolumeIcon,
  TotalOrdersIcon,
  UserParticipationIcon,

  // Notifications / Comms
  Notification,
  Mail,
  VerifyMail,
  Setting,
  Affiliate,

  // Game / Entertainment
  GameIcon,
  GameManagementIcon,
  GameOrdersIcon,
  PlaceGameIcon,
  ExperienceGameIcon,
  MaintenanceIcon,

  // Content / Structure
  Tree,
  Leader,
  Image,
  Reaction,
  Place,

  // Documents
  ClaimIcon,
  ChangePasswordIcon,

  // Organizations / Locations
  Building,
  Cooperative,
  BankBuilding,
  Storefront,

  // Points / Rewards
  Gift,
  PointTransaction,
  WithdrawRequest,
  ManageList,
  BulletList,

  // Checkbox
  CheckboxChecked,
  CheckboxUnchecked,

  // Loading
  Loading,

  // Misc
  Star,
};

// ---------------------------------------------------------------------------
// Generic  <GenericIcon name="..." size={20} className="..." />  API
// ---------------------------------------------------------------------------

type IconName = keyof typeof Icon;

interface GenericIconProps {
  name: IconName;
  size?: number;
  className?: string;
}

/**
 * Generic icon component.
 *
 * @example
 * <GenericIcon name="user" size={24} className="text-blue-500" />
 */
export function GenericIcon({
  name,
  size = 20,
  className,
}: GenericIconProps): JSX.Element {
  const Comp = Icon[name] as (props?: NamedIconProps) => JSX.Element;
  return <Comp size={size} className={className} />;
}
